import { React, useState, useEffect, useCallback } from "react";
import { Button, Input, notification } from "antd";
import { FilterOutlined, DownloadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";

import useRoleCheck from "utilities/hooks/roleCheck";
import axiosInstance from "utilities/axios";

import EquipmentTable from "./components/EquipmentTable";
import ViewEquipmentModal from "pages/ClinicDetails/ClinicEquipment/components/ViewEquipmentModal";
import FilterModal from "../shared/FilterModal";

import ForbiddenScreen from "pages/403";
import PrintButton from "./components/PrintButton";

const Inventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useRoleCheck();

  const search = searchParams.get("search") || "";

  const [content, setContent] = useState([]);
  const [inputValue, setInputValue] = useState(search);

  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    clinics: []
  });

  const fetchEquipmentItem = useCallback(() => {
    if (!selectedRecordId) {
      setSelectedRecord(null);
      return;
    }
    axiosInstance
      .get(`/equipment/inventory/${selectedRecordId}`)
      .then((result) => {
        setSelectedRecord(result.data);
      })

      .catch((err) => {
        console.error("Error fetching equipment data:", err);
      });
  }, [selectedRecordId]);

  useEffect(() => {
    fetchEquipmentItem();
  }, [selectedRecordId, fetchEquipmentItem]);

  const fetchInventory = useCallback(() => {
    const params = {};
    if (search) {
        params.search = search;
    }
    if (filters.clinics) {
        params.clinics = filters.clinics;
    }
    if (filters.category) {
        params.category = filters.category;
    }
    if (filters.brand) {
        params.brand = filters.brand;
    }
    if (filters.model) {
        params.model = filters.model;
    }

    axiosInstance
      .get("/equipment/inventory", { params })
      .then((response) => {
        setContent(response.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  }, [search, filters]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchParams(inputValue.trim() ? { search: inputValue } : {}, {
        replace: true,
      });
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, setSearchParams]);

  if (
    !hasPermission([
      "Operations",
      "IT",
      "Marketing",
      "Sales.RM",
      "Sales.Exec",
      "Audiology",
    ])
  ) {
    return <ForbiddenScreen />;
  }

  return (
    <>
      <div className="p-8">
        <div className="flex">
          <Input.Search
            placeholder="Search Equipment Inventory"
            allowClear
            size="large"
            suffix=""
            onChange={(event) => setInputValue(event.target.value)}
            value={inputValue}
            className="mr-2"
          />
          <Button
            type="primary"
            size="large"
            icon={<FilterOutlined />}
            className="ml-2"
            onClick={() => setIsFilterModalVisible(true)}
          >
            Filter
          </Button>
          <PrintButton search={search} filters={filters} />
        </div>

        <div className="mt-4">
          <EquipmentTable
            content={content}
            setSelectedItem={setSelectedRecordId}
          />
        </div>
      </div>

      <ViewEquipmentModal
        open={!!selectedRecordId}
        equipment={selectedRecord}
        onClose={() => setSelectedRecordId(null)}
        updateEquipment={() => {
          fetchInventory();
          fetchEquipmentItem();
        }}
      />
      <FilterModal
        visible={isFilterModalVisible}
        setVisible={setIsFilterModalVisible}
        setFilters={setFilters}
      />
    </>
  );
};

export default Inventory;
